import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { isEqual } from "lodash";

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";
import RentalInvoiceItemRow from "./RentalInvoiceItemRow";
import { categorizeItems, UNCATEGORIZED_LABEL } from "./categorizeItems";
import { mergeNestedEntry } from "./nestedAttributes";
import { formatCurrency } from "./currency";

const droppableIdFor = (group) =>
  group.category === UNCATEGORIZED_LABEL
    ? "category-uncategorized"
    : `category-named-${encodeURIComponent(group.category)}`;

const categoryNameFromDroppableId = (id) =>
  id === "category-uncategorized"
    ? UNCATEGORIZED_LABEL
    : decodeURIComponent(id.replace(/^category-named-/, ""));

const headerBorderBottom = "1px solid var(--sl-border-secondary, #e9eaeb)";

const columnHeaderStyle = {
  fontWeight: 600,
  color: "var(--sl-text-quaternary, #717680)",
  padding: "12px 12px",
  borderBottom: headerBorderBottom,
};

const RentalInvoiceItemsCard = () => {
  const {
    rentalInvoice,
    rentalInvoiceFormState,
    setRentalInvoiceFormState,
    insertInvoiceItem,
  } = useRentalInvoiceEditContext();

  const categorizedItems = categorizeItems(rentalInvoice.rental_invoice_items);
  const totalItemCount = rentalInvoice.rental_invoice_items.length;

  const [itemPositions, setItemPositions] = useState({});
  const [editingCategoryName, setEditingCategoryName] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);

  const constructItemPositions = (items) => {
    const positions = {};
    items.forEach((item) => { positions[item.id] = item.position; });
    return positions;
  };

  useEffect(() => {
    const persistedPositions = constructItemPositions(rentalInvoice.rental_invoice_items);

    if (!isEqual(persistedPositions, itemPositions)) {
      const existing = rentalInvoiceFormState.rental_invoice_items_attributes || [];
      const existingById = {};
      existing.forEach((entry) => {
        if (entry && entry.id != null) existingById[entry.id] = entry;
      });

      const merged = Object.keys(itemPositions).map((id) => ({
        ...(existingById[id] || {}),
        id: Number(id),
        position: itemPositions[id],
      }));

      setRentalInvoiceFormState({
        ...rentalInvoiceFormState,
        rental_invoice_items_attributes: merged
      });
    }
  }, [itemPositions]);

  useEffect(() => {
    setItemPositions(constructItemPositions(rentalInvoice.rental_invoice_items));
  }, [rentalInvoice.rental_invoice_items]);

  const onDragEnd = (result) => {
    if (!result.destination) return false;

    const { destination, draggableId } = result;

    const allItems = [...rentalInvoice.rental_invoice_items];
    const movedItem = allItems.find((i) => String(i.id) === draggableId);
    if (!movedItem) return false;

    const destCategoryName = categoryNameFromDroppableId(destination.droppableId);
    const newCategory = destCategoryName === UNCATEGORIZED_LABEL ? "" : destCategoryName;

    const updatedItems = allItems.map((item) => ({ ...item }));
    const draggedIndex = updatedItems.findIndex((i) => String(i.id) === draggableId);
    const [dragged] = updatedItems.splice(draggedIndex, 1);
    dragged.category = newCategory;

    const destSubsetIds = updatedItems
      .filter((i) => (i.category || "") === newCategory)
      .map((i) => i.id);
    const insertBeforeId = destSubsetIds[destination.index];

    let insertAt;
    if (insertBeforeId === undefined) {
      const lastIdxInDest = updatedItems
        .map((i, idx) => ((i.category || "") === newCategory ? idx : -1))
        .filter((idx) => idx !== -1)
        .pop();
      insertAt = lastIdxInDest === undefined ? updatedItems.length : lastIdxInDest + 1;
    } else {
      insertAt = updatedItems.findIndex((i) => i.id === insertBeforeId);
    }
    updatedItems.splice(insertAt, 0, dragged);

    const newPositions = {};
    updatedItems.forEach((item, idx) => { newPositions[item.id] = idx + 1; });

    if ((movedItem.category || "") !== newCategory) {
      setRentalInvoiceFormState({
        ...rentalInvoiceFormState,
        rental_invoice_items_attributes: [{
          id: movedItem.id,
          category: newCategory,
        }],
      });
    }

    setItemPositions(newPositions);
  };

  const addItemToCategory = (categoryName) => {
    const effectiveCategory = categoryName === UNCATEGORIZED_LABEL ? "" : categoryName;
    insertInvoiceItem({
      category: effectiveCategory,
      title: "",
      cost: 0,
      quantity: 1,
      cost_type: "fixed",
      position: Math.max(0, ...Object.values(itemPositions)) + 1,
    });
  };

  const commitCategoryRename = (oldName, rawNewName) => {
    const newName = (rawNewName || "").trim();
    setEditingCategoryName(null);
    if (!newName || newName === oldName) return;

    const group = categorizedItems.find((g) => g.category === oldName);
    if (!group) return;

    // Merge the category change into each affected row by id so pending edits to
    // items/fees in other groups aren't dropped (whole-bucket replace did drop them).
    let nextState = rentalInvoiceFormState;
    group.items.forEach((item) => {
      nextState = mergeNestedEntry(nextState, "rental_invoice_items_attributes", { id: item.id, category: newName });
    });
    (rentalInvoice.rental_invoice_tax_fees || [])
      .filter((fee) => fee.category === oldName)
      .forEach((fee) => {
        nextState = mergeNestedEntry(nextState, "rental_invoice_tax_fees_attributes", { id: fee.id, category: newName });
      });

    setRentalInvoiceFormState(nextState);
  };

  const commitAddCategory = (rawName) => {
    const name = (rawName || "").trim();
    setAddingCategory(false);
    if (!name) return;

    insertInvoiceItem({
      category: name,
      title: "",
      cost: 0,
      quantity: 1,
      cost_type: "fixed",
      position: Math.max(0, ...Object.values(itemPositions)) + 1,
    });
  };

  return (
    <div className="card">
      <div className="card-body">
        {totalItemCount > 0 ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <div style={{ overflowX: "auto" }}>
              <table className="table table-borderless" style={{ width: "100%", minWidth: 720 }}>
                <thead style={{ textAlign: "left" }}>
                  <tr>
                    <th style={{ borderBottom: headerBorderBottom }} />
                    <th className="setlist-text-xs figma-text quaternary" style={columnHeaderStyle}>Item</th>
                    <th className="setlist-text-xs figma-text quaternary" style={{ ...columnHeaderStyle, textAlign: "right" }}>Qty</th>
                    <th className="setlist-text-xs figma-text quaternary" style={{ ...columnHeaderStyle, textAlign: "right" }}>Type</th>
                    <th className="setlist-text-xs figma-text quaternary" style={{ ...columnHeaderStyle, textAlign: "right" }}>Rate</th>
                    <th className="setlist-text-xs figma-text quaternary" style={{ ...columnHeaderStyle, textAlign: "right" }}>Total</th>
                    <th style={{ borderBottom: headerBorderBottom }} />
                  </tr>
                </thead>
                {categorizedItems.map((group) => (
                  <Droppable
                    key={droppableIdFor(group)}
                    droppableId={droppableIdFor(group)}
                  >
                    {(provided) => (
                      <tbody
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        <tr style={{ background: "var(--sl-bg-secondary, #fafafa)" }}>
                          <td colSpan={6} style={{ padding: "8px 12px", verticalAlign: "middle", borderBottom: "1px solid var(--sl-border-secondary, #e9eaeb)" }}>
                            {editingCategoryName === group.category ? (
                              <input
                                autoFocus
                                type="text"
                                className="form-control setlist-text-md setlist-font-semibold"
                                style={{ width: 240, display: "inline-block" }}
                                defaultValue={group.category}
                                onBlur={(e) => commitCategoryRename(group.category, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === "Escape") e.target.blur();
                                }}
                              />
                            ) : (
                              <>
                                <span
                                  onClick={() => {
                                    if (group.category !== UNCATEGORIZED_LABEL) {
                                      setEditingCategoryName(group.category);
                                    }
                                  }}
                                  className={`setlist-text-md setlist-font-semibold ${group.category === UNCATEGORIZED_LABEL ? "figma-text quinary" : ""}`}
                                  style={{ cursor: group.category === UNCATEGORIZED_LABEL ? "default" : "pointer" }}
                                >
                                  {group.category}
                                </span>
                                <span className="setlist-text-sm figma-text quaternary ml-2">
                                  {group.items.length} item{group.items.length === 1 ? "" : "s"}
                                </span>
                              </>
                            )}
                          </td>
                          <td style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid var(--sl-border-secondary, #e9eaeb)" }}>
                            <div className="setlist-text-xs figma-text quaternary" style={{ letterSpacing: "0.06em" }}>SUBTOTAL</div>
                            <div className="setlist-text-md setlist-font-semibold">{formatCurrency(group.subtotal)}</div>
                          </td>
                        </tr>
                        {group.items
                          .slice()
                          .sort((a, b) => (itemPositions[a.id] || 0) - (itemPositions[b.id] || 0))
                          .map((rentalInvoiceItem, index) => (
                            <Draggable
                              key={"" + rentalInvoiceItem.id}
                              draggableId={"" + rentalInvoiceItem.id}
                              index={index}
                            >
                              {(provided) => (
                                <RentalInvoiceItemRow
                                  rentalInvoiceItem={rentalInvoiceItem}
                                  provided={provided}
                                />
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                        <tr>
                          <td colSpan={7} style={{ padding: "8px 12px 25px" }}>
                            <a
                              href="#"
                              onClick={(e) => { e.preventDefault(); addItemToCategory(group.category); }}
                              className="setlist-text-sm figma-text link"
                              style={{ textDecoration: "none" }}
                            >
                              <i className="far fa-plus mr-1" />
                              {group.category === UNCATEGORIZED_LABEL ? "Add item" : `Add item to ${group.category}`}
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </Droppable>
                ))}
              </table>
            </div>
          </DragDropContext>
        ) : null}

        <div className="d-flex align-items-center justify-content-between mt-3">
          {addingCategory ? (
            <input
              autoFocus
              type="text"
              className="form-control"
              style={{ maxWidth: 240 }}
              placeholder="Category name"
              onBlur={(e) => commitAddCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") e.target.blur();
              }}
            />
          ) : (
            <button
              className="btn btn-setlist-secondary"
              type="button"
              onClick={() => setAddingCategory(true)}
            >
              <i className="far fa-plus mr-1" /> Add category
            </button>
          )}
          <div className="setlist-text-sm figma-text quinary">
            Drag rows to reorder · Click a category name to rename
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalInvoiceItemsCard;
